/**
 * Lo que queda a medias en Configuración, contado una sola vez.
 *
 * Lo miran dos sitios a la vez —el índice de Configuración, para su pastilla de
 * cada entrada, y el menú de la izquierda, para el aviso de la entrada— y por
 * eso vive aquí arriba y no dentro de la pantalla: con una consulta en cada uno
 * el mismo dato se pedía dos veces y podía contestar distinto en cada sitio.
 *
 * La cuenta la hace el servidor, que es donde están los documentos. Aquí vivían
 * la expresión de los marcadores y la resta de los datos del negocio, o sea una
 * segunda implementación de la misma regla: cualquier marcador nuevo había que
 * acordarse de añadirlo en los dos lados.
 *
 * Cada pieza es nula mientras no se sepa, y se queda nula si la consulta falla.
 * Es lo que evita el peor error posible aquí: decir «Completa» de algo que no
 * hemos podido mirar.
 */
import { useEffect, useState } from 'react'
import { getSetupAttention } from './api'
import { onConfigAttentionChanged } from './attentionSignal'

export type ConfigAttention = {
  /** Cuántas políticas no se han escrito y cuántos huecos quedan en las escritas. */
  policies: { unwritten: number; holes: number } | null
  /** Cuántos datos del negocio siguen vacíos. */
  business: { missing: number } | null
}

/** Todo lo que falta, junto. Es lo que cuenta el aviso del menú. */
export function attentionCount(attention: ConfigAttention): number {
  const policies = attention.policies ? attention.policies.unwritten + attention.policies.holes : 0
  return policies + (attention.business?.missing ?? 0)
}

const NOTHING: ConfigAttention = { policies: null, business: null }

export function useConfigAttention(projectId: string, token: string): ConfigAttention {
  const [attention, setAttention] = useState<ConfigAttention>(NOTHING)
  /* Sube cada vez que algo guardado cambia la cuenta: ver `attentionSignal`. */
  const [version, setVersion] = useState(0)

  useEffect(() => onConfigAttentionChanged((changed) => {
    if (changed === projectId) setVersion((value) => value + 1)
  }), [projectId])

  /* Al cambiar de proyecto se olvida lo sabido: la cuenta del anterior no dice
     nada de éste. Al volver a preguntar por el MISMO proyecto, en cambio, se
     deja la pastilla como estaba hasta que llega la respuesta, en vez de
     hacerla parpadear. */
  useEffect(() => { setAttention(NOTHING) }, [projectId, token])

  useEffect(() => {
    let alive = true

    getSetupAttention(token, projectId)
      .then((data) => {
        if (!alive) return
        /* Mientras no haya nada que cobrar no falta nada, y devolver nulos —el
           mismo estado que «todavía no lo sé»— apaga de golpe la pastilla de
           cada tarjeta y el aviso del menú, sin que ninguno de los dos tenga
           que enterarse de por qué. El razonamiento está en el servidor, en
           `GET /:id/setup-attention`: un portafolio nace con su privacidad y
           sus términos, y pedirle además el RFC y una política de reembolsos
           es reclamarle papeles de un trámite que no ha empezado.

           Basta con que una oferta pase a activa para que aparezca todo.

           Con una excepción: una política que ya existe y no se publica por un
           hueco. Esa sí falta hoy —el sitio recoge datos por su formulario y no
           enlaza su aviso de privacidad—, venda o no. */
        if (!data.sells) {
          setAttention(data.policies.holes > 0
            ? { policies: { unwritten: 0, holes: data.policies.holes }, business: null }
            : NOTHING)
          return
        }
        setAttention({ policies: data.policies, business: data.business })
      })
      .catch(() => {})

    return () => { alive = false }
  }, [projectId, token, version])

  return attention
}
